<template>
    <v-container fluid class="pa-6">
      <!-- Cabecera -->
      <v-row class="mb-4" dense>
        <v-col cols="12">
          <div class="d-flex flex-column flex-md-row align-start align-md-center justify-space-between" style="gap: 16px;">
            <div>
              <h1 class="headline font-weight-bold mb-1">Calendario Laboral</h1>
              <p class="body-2 subtitle-1 grey--text text--darken-1">
                Marca los días festivos haciendo clic
              </p>
            </div>
            
            <!-- Contenedor de botones -->
            <div class="d-flex flex-column flex-sm-row" style="gap: 8px; width: 100%; max-width: 800px;">
              <v-btn
                depressed
                color="info"
                @click="yearDialog = true"
                class="flex-grow-1"
              >
                <v-icon left>mdi-calendar-month-outline</v-icon>
                <span class="hidden-xs-only">Vista Anual</span> {{ currentYearView }}
              </v-btn>
  
              <v-btn
                outlined
                :color="hasWeekendsSelected ? 'orange darken-2' : 'secondary'"
                @click="toggleWeekends"
                class="flex-grow-1"
              >
                <v-icon left>{{ hasWeekendsSelected ? 'mdi-calendar-remove' : 'mdi-calendar-week' }}</v-icon>
                <span class="d-sm-none">{{ hasWeekendsSelected ? 'Quitar Fines' : 'Poner Fines' }}</span>
                <span class="d-none d-sm-inline">{{ hasWeekendsSelected ? 'Quitar Fines de Semana' : 'Seleccionar Fines de Semana' }}</span>
              </v-btn>
  
              <v-btn
                color="primary"
                elevation="2"
                :loading="isSaving"
                :disabled="selectedDates.length === 0"
                @click="saveHolidays"
                class="flex-grow-1"
              >
                <v-icon left>mdi-content-save</v-icon>
                Guardar
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>
  
      <v-row>
        <!-- Calendario -->
        <v-col cols="12" md="7" lg="8">
          <v-card outlined class="rounded-xl overflow-hidden">
            <!-- En Vuetify 2, v-date-picker multiple usa strings ['YYYY-MM-DD'] -->
            <v-date-picker
              v-model="selectedDates"
              multiple
              full-width
              color="primary"
              locale="es"
              :first-day-of-week="1"
              :allowed-dates="isSelectable"
              :events="savedDatesStrings"
              event-color="red"
            ></v-date-picker>
          </v-card>
        </v-col>
  
        <!-- Listado lateral -->
        <v-col cols="12" md="5" lg="4">
          <v-card outlined class="rounded-xl d-flex flex-column h-100" style="max-height: 550px;">
            <v-card-title class="subtitle-1">
              Resumen de Festivos
              <v-spacer></v-spacer>
              <v-chip color="primary" small dark>
                {{ selectedDates.length }} seleccionados
              </v-chip>
              <v-btn
                text
                small
                color="error"
                class="text-none font-weight-medium"
                :disabled="selectedDates.length === 0"
                @click="clearAllSelected"
              >
                <v-icon left small>mdi-delete-sweep-outline</v-icon>
                Limpiar
              </v-btn>
            </v-card-title>
  
            <v-divider></v-divider>
  
            <!-- INPUT MANUAL -->
            <div class="pa-4 blue lighten-5">
              <v-text-field
                v-model="manualDateInput"
                label="Añadir fecha (DD/MM/YYYY)"
                placeholder="Ej: 01/05/2026"
                outlined
                dense
                hide-details
                background-color="white"
                append-icon="mdi-plus-circle"
                @keyup.enter="addManualDate"
                @click:append="addManualDate"
              ></v-text-field>
              <div v-if="manualDateError" class="caption error--text mt-1 ml-1">
                {{ manualDateError }}
              </div>
            </div>
  
            <v-divider></v-divider>
  
            <v-list class="overflow-y-auto flex-grow-1 pt-0" style="max-height: 300px;">
              <v-list-item v-if="selectedDates.length === 0" class="text-center py-12">
                <v-list-item-content>
                  <v-icon large color="grey lighten-2" class="mb-2">mdi-calendar-remove</v-icon>
                  <div class="body-2 grey--text">No has marcado ningún día aún</div>
                </v-list-item-content>
              </v-list-item>
  
              <v-list-item
                v-for="(date, index) in sortedSelected"
                :key="index"
                class="border-bottom"
              >
                <v-list-item-icon class="mr-2">
                  <v-icon color="error" small>mdi-circle-medium</v-icon>
                </v-list-item-icon>
  
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium capitalize-first">
                    {{ formatDate(date) }}
                  </v-list-item-title>
                </v-list-item-content>
  
                <v-list-item-action>
                  <v-btn icon small @click="removeDate(date)">
                    <v-icon color="grey lighten-1">mdi-close-circle-outline</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
  
            <v-divider></v-divider>
            <div class="pa-4 grey lighten-4">
              <div class="d-flex align-center caption grey--text text--darken-1">
                <v-icon small class="mr-1">mdi-information-outline</v-icon>
                Los cambios solo se aplicarán al pulsar "Guardar".
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
  
      <!-- TABLA DE FESTIVOS -->
      <v-row class="mt-10">
        <v-col cols="12">
          <v-card outlined class="rounded-xl">
            <v-card-title class="pa-4">
              <span class="headline">Listado de Festivos</span>
              <v-spacer></v-spacer>
              
              <v-chip 
                v-if="filterRange.length > 0" 
                color="primary" 
                close 
                @click:close="filterRange = []" 
                class="mr-2"
              >
                {{ filterRange.length === 1 ? 'Día: ' + formatShortDate(filterRange[0]) : 'Rango' }}
              </v-chip>
  
              <v-menu offset-y :close-on-content-click="false">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn color="primary" outlined v-bind="attrs" v-on="on">
                    <v-icon left>mdi-filter-variant</v-icon> Filtrar por rango
                  </v-btn>
                </template>
                <v-card>
                  <v-date-picker v-model="filterRange" range locale="es" color="primary"></v-date-picker>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="filterRange = []">Limpiar</v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-card-title>
  
            <v-data-table
              :headers="tableHeaders"
              :items="filteredSavedDates"
              class="elevation-0"
            >
              <template v-slot:item.date="{ item }">
                {{ formatDate(item.date) }}
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn icon color="red" text @click="deleteSavedDate(item.date)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
  
      <!-- DIALOGO VISTA ANUAL -->
      <v-dialog v-model="yearDialog" fullscreen transition="dialog-bottom-transition">
        <v-card class="d-flex flex-column" style="overflow: hidden;">
          <v-toolbar color="grey lighten-4" dense flat outlined>
            <v-btn icon @click="currentYearView--"><v-icon>mdi-chevron-left</v-icon></v-btn>
            <v-toolbar-title class="text-center font-weight-black">
              {{ currentYearView }}
            </v-toolbar-title>
            <v-btn icon @click="currentYearView++"><v-icon>mdi-chevron-right</v-icon></v-btn>
            <v-spacer></v-spacer>
            <v-btn icon @click="yearDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
  
          <v-card-text class="year-grid-container pa-1 bg-white">
            <div class="year-wrapper">
              <div v-for="month in 12" :key="month" class="compact-month">
                <div class="month-title">{{ getMonthName(month - 1) }}</div>
                <div class="compact-days-grid">
                  <div v-for="d in ['L', 'M', 'X', 'J', 'V', 'S', 'D']" :key="d" class="compact-day-header">{{ d }}</div>
                  <div v-for="blank in getFirstDayOfMonth(month - 1)" :key="'b'+blank" class="compact-day-cell empty"></div>
                  <div 
                    v-for="day in getDaysInMonth(month - 1)" 
                    :key="day" 
                    class="compact-day-cell"
                    :class="getDayStatusClass(day, month - 1)"
                  >
                    {{ day }}
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
  
      <!-- SNACKBAR -->
      <v-snackbar v-model="notify.show" :color="notify.color" :timeout="notify.timeout" top>
        <v-icon left dark>{{ notify.icon }}</v-icon>
        {{ notify.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="notify.show = false">Cerrar</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </template>
  
  <script>
import axios from 'axios'; // Asegúrate de tener axios importado

export default {
    name: 'CalendarioLaboral',
    data() {
      return {
        selectedDates: [], 
        savedDates: [],    
        currentYearView: new Date().getFullYear(),
        yearDialog: false,
        manualDateInput: '',
        manualDateError: '',
        isSaving: false,
        filterRange: [],
        notify: {
          show: false,
          text: '',
          color: 'success',
          icon: 'mdi-check-circle',
          timeout: 3000
        },
        tableHeaders: [
          { text: "Fecha", value: "date" },
          { text: "Acciones", value: "actions", sortable: false, align: 'end' }
        ]
      }
    },
    computed: {
      // Obtenemos el ID de la empresa del localStorage
      idEmpresa() {
        return localStorage.getItem("clienteSeleccionado");
      },
      savedDatesStrings() {
        return this.savedDates.map(d => {
          const date = new Date(d);
          return date.toISOString().substr(0, 10);
        });
      },
      hasWeekendsSelected() {
        return this.selectedDates.some(dateStr => {
          const day = new Date(dateStr).getDay();
          return day === 0 || day === 6;
        });
      },
      sortedSelected() {
        return [...this.selectedDates].sort((a, b) => new Date(a) - new Date(b));
      },
      filteredSavedDates() {
        if (!this.filterRange || this.filterRange.length === 0) {
          return this.savedDates.map(d => ({ date: d }));
        }
        const sorted = [...this.filterRange].sort();
        const start = new Date(sorted[0]);
        const end = sorted.length === 2 ? new Date(sorted[1]) : new Date(sorted[0]);
        end.setHours(23, 59, 59);
  
        return this.savedDates
          .filter(d => {
            const check = new Date(d);
            return check >= start && check <= end;
          })
          .map(d => ({ date: d }))
          .sort((a, b) => new Date(a.date) - new Date(b.date));
      }
    },
    mounted() {
      this.loadSavedHolidays();
    },
    methods: {
      showNotify(text, color = 'success') {
        this.notify.text = text;
        this.notify.color = color;
        this.notify.icon = color === 'error' ? 'mdi-alert-circle' : 'mdi-check-circle';
        this.notify.show = true;
      },
      async loadSavedHolidays() {
        // Si no hay empresa seleccionada, no hacemos la petición
        if (!this.idEmpresa) return;

        try {
          const res = await axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/fechas-festivo`, {
            params: { id_empresa: this.idEmpresa } // Enviado como query param (?id_empresa=...)
          });
          this.savedDates = res.data.festivos.map(f => new Date(f));
        } catch (e) { 
            console.error(e);
            this.showNotify('Error al cargar festivos', 'error');
        }
      },
      async deleteSavedDate(date) {
        if (!(await this.$confirmar({ titulo: "Eliminar festivo", texto: "Se eliminará este festivo del calendario.", peligro: true }))) return;
        try {
          await axios.delete(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/borrar-festivo`, { 
            data: { 
                fecha: date, 
                id_empresa: this.idEmpresa // Enviado en el body del DELETE
            } 
          });
          this.showNotify('Eliminado', 'info');
          this.loadSavedHolidays();
        } catch (e) { this.showNotify('Error al eliminar', 'error'); }
      },
      async saveHolidays() {
        if (this.selectedDates.length === 0) return;
        this.isSaving = true;

        try {
          await axios.post(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/crear-festivos`, { 
            fechas: this.selectedDates, 
            id_empresa: this.idEmpresa // Enviado en el body del POST
          });
          this.showNotify('Guardado correctamente');
          this.selectedDates = [];
          this.loadSavedHolidays();
        } catch (e) { this.showNotify('Error al guardar', 'error'); }
        finally { this.isSaving = false; }
      },
      // Resto de métodos de lógica visual se mantienen igual
      isSelectable(date) {
        return !this.savedDatesStrings.includes(date);
      },
      formatDate(date) {
        return new Intl.DateTimeFormat('es-ES', {
          weekday: 'short', day: 'numeric', month: 'long', year: 'numeric'
        }).format(new Date(date));
      },
      formatShortDate(date) {
        return new Intl.DateTimeFormat('es-ES', {
          day: '2-digit', month: '2-digit', year: '2-digit'
        }).format(new Date(date));
      },
      addManualDate() {
        this.manualDateError = '';
        const regex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
        const match = this.manualDateInput.match(regex);
        if (!match) { this.manualDateError = 'Formato inválido (DD/MM/YYYY)'; return; }
  
        const [_, d, m, y] = match;
        const dateObj = new Date(y, m - 1, d);
        const isoStr = dateObj.toISOString().substr(0, 10);
  
        if (this.savedDatesStrings.includes(isoStr)) {
          this.manualDateError = 'Ya está guardada';
        } else if (!this.selectedDates.includes(isoStr)) {
          this.selectedDates.push(isoStr);
          this.manualDateInput = '';
        }
      },
      toggleWeekends() {
        if (this.hasWeekendsSelected) {
          // QUITAR: Filtramos y nos quedamos solo con los que NO son sábado (6) ni domingo (0)
          this.selectedDates = this.selectedDates.filter(d => {
            const day = new Date(d).getDay();
            return day !== 0 && day !== 6;
          });
        } else {
          // AÑADIR:
          // Usamos el año que el usuario está visualizando en el componente
          const year = this.currentYearView || new Date().getFullYear();
          const newOnes = [...this.selectedDates];

          for (let m = 0; m < 12; m++) {
            // Creamos la fecha al mediodía (12:00) para evitar problemas con cambios de hora (DST)
            let d = new Date(year, m, 1, 12, 0, 0);

            while (d.getMonth() === m) {
              const dayOfWeek = d.getDay();

              if (dayOfWeek === 0 || dayOfWeek === 6) {
                // Formateamos manualmente a YYYY-MM-DD para evitar el desfase de toISOString()
                const yearStr = d.getFullYear();
                const monthStr = String(d.getMonth() + 1).padStart(2, '0');
                const dayStr = String(d.getDate()).padStart(2, '0');
                const s = `${yearStr}-${monthStr}-${dayStr}`;

                // Solo añadimos si no está ya guardado en BBDD y no está ya seleccionado
                if (!this.savedDatesStrings.includes(s) && !newOnes.includes(s)) {
                  newOnes.push(s);
                }
              }
              d.setDate(d.getDate() + 1);
            }
          }
          this.selectedDates = newOnes;
        }
      },
      removeDate(dateStr) {
        this.selectedDates = this.selectedDates.filter(d => d !== dateStr);
      },
      async clearAllSelected() {
        if (await this.$confirmar({ titulo: "Limpiar selección", texto: "Se quitarán todas las fechas seleccionadas.", aceptar: "Limpiar" })) this.selectedDates = [];
      },
      getMonthName(idx) { return new Intl.DateTimeFormat('es-ES', { month: 'long' }).format(new Date(2024, idx, 1)); },
      getDaysInMonth(idx) { return new Date(this.currentYearView, idx + 1, 0).getDate(); },
      getFirstDayOfMonth(idx) { 
        let d = new Date(this.currentYearView, idx, 1).getDay();
        return d === 0 ? 6 : d - 1;
      },
      getDayStatusClass(day, month) {
        const d = new Date(this.currentYearView, month, day);
        const s = d.toISOString().substr(0, 10);
        if (this.savedDatesStrings.includes(s)) return 'day-saved';
        if (this.selectedDates.includes(s)) return 'day-selected';
        if (d.getDay() === 0 || d.getDay() === 6) return 'day-weekend';
        return '';
      }
    }
}
  </script>
  
  <style scoped>
  .rounded-xl { border-radius: 16px !important; }
  .border-bottom { border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important; }
  .capitalize-first::first-letter { text-transform: uppercase; }
  
  /* Vista Anual */
  .year-grid-container { height: calc(100vh - 64px); overflow-y: auto; background-color: #f8f9fa; }
  .year-wrapper { 
    display: grid; 
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); 
    gap: 12px; 
    padding: 12px; 
  }
  .compact-month { border: 1px solid #e0e0e0; border-radius: 8px; background: white; padding: 8px; }
  .month-title { text-align: center; font-weight: bold; color: #1976D2; text-transform: uppercase; margin-bottom: 4px; }
  .compact-days-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
  .compact-day-header { font-size: 0.6rem; text-align: center; color: #9e9e9e; font-weight: bold; }
  .compact-day-cell { aspect-ratio: 1/1; display: flex; align-items: center; justify-content: center; font-size: 0.7rem; border-radius: 4px; }
  
  .day-saved { background-color: #FFEBEE !important; color: #D32F2F !important; font-weight: bold; }
  .day-selected { background-color: #E3F2FD !important; color: #1976D2 !important; font-weight: bold; border: 1px solid #2196F3; }
  .day-weekend { background-color: #FAFAFA; color: #BDBDBD; }
  
  ::v-deep .v-date-picker-table { height: auto; }
  </style>